/**
 * Seasonal (festive) theme — the body-class switch and the asset map.
 *
 * The backend decides the window (`/feature-flags` → `seasonal_theme`, see
 * `services/seasonal_theme.py`); this module only turns that answer into
 * `body.theme-<key>` so `festive.css` can restyle, and keeps Checkout, Cart,
 * Login and the staff / admin surfaces plain via `body.festive-off`.
 *
 * Boot flash: the flags fetch lands after the first paint, so the last answer
 * is remembered in localStorage and re-applied synchronously from `index.js`
 * (only while today is still inside the remembered window). Nothing here
 * changes layout — colour, a header restyle and absolutely-positioned images.
 */

export const SEASONAL_CACHE_KEY = 'pb_seasonal_theme';

/** Routes that never wear the theme — transactional, auth, staff and admin surfaces. */
export const FESTIVE_OFF_PATHS = ['/cart', '/checkout', '/login', '/signup', '/kiosk', '/admin', '/investor', '/staff', '/receipt', '/f/'];

/** Image assets per theme key, served statically from `public/festive/<key>/`. */
export const SEASONAL_ASSETS = {
  diwali: {
    lanterns: '/festive/diwali/lanterns.webp',
    marigolds: '/festive/diwali/marigolds.webp',
    mandala: '/festive/diwali/mandala.webp',
  },
};

/** True when `pathname` is one of the plain (no-theme) routes. */
export function isFestiveOffPath(pathname) {
  const p = pathname || '';
  return FESTIVE_OFF_PATHS.some((prefix) => p.startsWith(prefix));
}

/** Today as an IST `YYYY-MM-DD`, the same calendar the backend decides on. */
export function todayIST(now = new Date()) {
  const ist = new Date(now.getTime() + (now.getTimezoneOffset() + 330) * 60000);
  return ist.toISOString().slice(0, 10);
}

/**
 * Apply `seasonal` (the `/feature-flags` object) to `document.body`: toggles
 * `theme-<key>` on `active`, and `festive-off` from `pathname`. Returns the
 * active state so callers can branch without re-reading the DOM.
 */
export function applySeasonalTheme(seasonal, pathname) {
  if (typeof document === 'undefined') return false;
  const body = document.body;
  const key = seasonal?.key || 'diwali';
  const active = !!seasonal?.active;
  [...body.classList].forEach((c) => { if (c.startsWith('theme-') && c !== `theme-${key}`) body.classList.remove(c); });
  body.classList.toggle(`theme-${key}`, active);
  body.classList.toggle('festive-off', isFestiveOffPath(pathname));
  return active;
}

/** Remember the last `/feature-flags` answer so the next boot can paint it before the fetch. */
export function rememberSeasonalTheme(seasonal) {
  try {
    if (!seasonal) return;
    localStorage.setItem(SEASONAL_CACHE_KEY, JSON.stringify({ key: seasonal.key, active: !!seasonal.active, start: seasonal.start, end: seasonal.end }));
  } catch { /* storage unavailable — the fetch still applies it */ }
}

/**
 * Boot-time pre-paint: re-apply the remembered theme if today is still inside
 * the remembered window. Wrong for at most one load after admin turns it off
 * early, and corrected the moment the flags arrive.
 */
export function applyCachedSeasonalTheme(pathname) {
  try {
    const raw = localStorage.getItem(SEASONAL_CACHE_KEY);
    if (!raw) return false;
    const cached = JSON.parse(raw);
    const today = todayIST();
    const inWindow = cached.active && cached.start && cached.end && cached.start <= today && today <= cached.end;
    return applySeasonalTheme({ key: cached.key, active: inWindow }, pathname);
  } catch {
    return false;
  }
}
